import styled, { css } from 'styled-components'

import { mq, sizes } from '../../theme'

import { ECollapsable } from './types'

type StyledProps = {
  backgroundColor?: string
  isOpen: boolean
  isCollapsable: ECollapsable
  rounded?: boolean
  backgroundImage?: string
}

export const Container = styled.section<StyledProps>`
  position: relative;
  z-index: ${({ theme }) => theme.zIndexes.minimalContent};
  margin: 0;
  min-height: 7.5rem;
  padding-top: 2.5rem;
  border-radius: ${({ rounded }) => (rounded ? '3rem 3rem 0 0' : '0')};
  overflow: hidden;
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor ?? theme.colors.pureWhite};

  &:last-of-type {
    padding-bottom: 2.5rem;
  }

  &:not(:last-of-type) {
    padding-bottom: 10rem;
  }

  &:not(:first-child) {
    margin-top: -7.5rem;
    box-shadow: 0 -0.8rem 2rem rgba(0, 0, 0, 0.05);
  }

  &::before {
    ${({ backgroundImage }) =>
      backgroundImage &&
      css`
        content: '';
        z-index: -1;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background-image: url(${backgroundImage});
        background-size: cover;
        background-position: center;
        background-repeat: repeat;
        opacity: 0.5;
      `}
  }

  @media ${mq(sizes.desktop)} {
    padding-top: 6rem;
    border-radius: ${({ rounded }) => (rounded ? '6rem 6rem 0 0' : '0')};
  }
  @media print {
    margin-top: 0 !important;
    padding-bottom: 1.5rem !important;
    padding-top: 1.5rem !important;
  }
`

export const Header = styled.header<{ isCollapsable: ECollapsable }>`
  display: flex;
  align-items: center;
  min-height: ${({ isCollapsable }) =>
    isCollapsable === ECollapsable.BOTH || isCollapsable === ECollapsable.MOBILE
      ? '3.5rem'
      : '0'};
  padding-left: 2rem;
  padding-right: 2rem;
  position: relative;
  @media ${mq(sizes.desktop)} {
    min-height: 0;
    padding-left: 8rem;
    padding-right: 8rem;
  }
`

export const HeaderContent = styled.div<{ isCollapsable: ECollapsable }>`
  flex: 1 1 auto;
  display: flex;
  max-width: ${({ theme }) => `${theme.sizes.boxContent / 10}rem`};
  @media ${mq(0, sizes.desktop - 1)} {
    ${({ isCollapsable }) =>
      isCollapsable === ECollapsable.BOTH ||
      isCollapsable === ECollapsable.DESKTOP
        ? css`
            justify-content: center;
          `
        : css`
            & > * {
              flex: 1 1 auto;
            }
          `}
  }
  @media ${mq(sizes.desktop)} {
    margin-left: auto;
    margin-right: auto;
    & > * {
      flex: 1 1 auto;
    }
  }
`

export const Trigger = styled.div<StyledProps>`
  flex: 0 0 auto;
  display: ${({ isCollapsable }) =>
    isCollapsable === ECollapsable.MOBILE || isCollapsable === ECollapsable.BOTH
      ? 'flex'
      : 'none'};
  margin-left: 2rem;
  transition: transform 100ms ease-in-out;
  position: absolute;
  inset: 0 2rem auto auto;
  transform: ${({ isOpen }) => (isOpen === false ? '' : 'rotate(180deg)')};

  @media ${mq(sizes.desktop)} {
    display: ${({ isCollapsable }) =>
      isCollapsable === ECollapsable.DESKTOP ||
      isCollapsable === ECollapsable.BOTH
        ? 'flex'
        : 'none'};
  }
  @media print {
    display: none;
  }
`

export const Content = styled.div<StyledProps>`
  max-width: ${({ theme }) =>
    `calc(${theme.sizes.boxContent / 10}rem + 8rem + 8rem)`};
  margin-left: auto;
  margin-right: auto;
  max-height: ${({ isCollapsable, isOpen }) =>
    (isCollapsable === ECollapsable.MOBILE ||
      isCollapsable === ECollapsable.BOTH) &&
    !isOpen
      ? '0'
      : 'none'};
  padding-left: 2rem;
  padding-right: 2rem;
  overflow: hidden;
  opacity: ${({ isOpen }) => (isOpen === false ? '0' : '1')};
  transition: opacity 300ms ease-in-out;
  @media ${mq(sizes.desktop)} {
    max-height: ${({ isCollapsable, isOpen }) =>
      (isCollapsable === ECollapsable.DESKTOP ||
        isCollapsable === ECollapsable.BOTH) &&
      !isOpen
        ? '0'
        : 'none'};
    ${({ isCollapsable, isOpen }) =>
      (isCollapsable === ECollapsable.DESKTOP ||
        isCollapsable === ECollapsable.BOTH) &&
      !isOpen
        ? css`
            max-height: 0;
            opacity: 0;
          `
        : css`
            max-height: none;
            opacity: 1;
          `};
    padding-left: 8rem;
    padding-right: 8rem;
  }
`
